import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '@/lib/axiosInstance';
import {
  Pulse as Activity,
  ArrowRight,
  ArrowsClockwise as RefreshCw,
  Broadcast as Radio,
  Bug,
  Clock,
  Command,
  Database,
  DownloadSimple as Download,
  FileArrowDown as FileDown,
  FileText,
  GearSix as Settings,
  Graph as Network,
  HardDrives as Server,
  Heartbeat as HeartPulse,
  House as Home,
  Key,
  Lightning as Zap,
  MagnifyingGlass as Search,
  Monitor,
  Play,
  Shield,
  Star,
  Target,
  Users,
  Wrench,
  X
} from '@phosphor-icons/react';

const RECENT_SEARCHES_KEY = 'commandPalette_recentSearches';
const MAX_RECENT_SEARCHES = 5;

const CommandPalette = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [recentSearches, setRecentSearches] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const inputRef = useRef(null);
  const navigate = useNavigate();
  // Agentes REALES del cliente (antes: 5 agentes inventados, auditoría vibe
  // 2026-10-07 FE-04). Se piden al abrir la paleta.
  const [agents, setAgents] = useState([]);

  // Load recent searches from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
      if (stored) {
        setRecentSearches(JSON.parse(stored));
      }
    } catch (e) {
      // silencio-ok: historial local de búsquedas ilegible; la paleta funciona sin él
      console.error('Error loading recent searches:', e);
    }
  }, []);

  // Save recent search
  const saveRecentSearch = useCallback((item) => {
    setRecentSearches(prev => {
      const filtered = prev.filter(s => s.path !== item.path);
      const updated = [item, ...filtered].slice(0, MAX_RECENT_SEARCHES);
      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const pages = [
    { name: 'Dashboard', path: '/', icon: Home, keywords: ['inicio', 'home', 'panel'], category: 'pages' },
    { name: 'Agents', path: '/agents', icon: Server, keywords: ['agentes', 'servidores', 'endpoints'], category: 'pages' },
    { name: 'Agent Health', path: '/agent-health', icon: HeartPulse, keywords: ['salud', 'health', 'estado'], category: 'pages' },
    { name: 'Network Map', path: '/network', icon: Network, keywords: ['red', 'mapa', 'topologia'], category: 'pages' },
    { name: 'Network Scanner', path: '/network-scanner', icon: Search, keywords: ['escanear', 'scan', 'descubrir'], category: 'pages' },
    { name: 'Vulnerabilities', path: '/vulnerabilities', icon: Shield, keywords: ['cve', 'seguridad', 'riesgos'], category: 'pages' },
    { name: 'DLP', path: '/dlp', icon: Database, keywords: ['data loss', 'prevencion', 'fugas'], category: 'pages' },
    { name: 'AD Attacks', path: '/ad-attacks', icon: Key, keywords: ['active directory', 'kerberos', 'ldap'], category: 'pages' },
    // Retirada T5 (2026-10-13): fuera 'Incident Response' (duplicaba Vanguard y no
    // persistia nada) y 'Human Layer' (todo sintetico). Sus pantallas ya no existen.
    // Candado: backend/tests/test_retirado_t5_pantallas.py.
    { name: 'Remediation', path: '/remediation', icon: Wrench, keywords: ['arreglar', 'fix', 'solucion'], category: 'pages' },
    { name: 'Data Leaks', path: '/data-leaks', icon: Database, keywords: ['fugas', 'breach', 'leaked'], category: 'pages' },
    { name: 'Metrics', path: '/metrics', icon: Activity, keywords: ['metricas', 'cpu', 'memoria', 'rendimiento'], category: 'pages' },
    { name: 'Reports', path: '/reports', icon: FileText, keywords: ['informes', 'pdf', 'exportar'], category: 'pages' },
    { name: 'SIEM Integrations', path: '/siem-integrations', icon: Radio, keywords: ['splunk', 'sentinel', 'qradar'], category: 'pages' },
    { name: 'Users', path: '/users', icon: Users, keywords: ['usuarios', 'admin', 'roles'], category: 'pages' },
    { name: 'Settings', path: '/settings', icon: Settings, keywords: ['configuracion', 'ajustes', 'config'], category: 'pages' },
    { name: 'Download Agent', path: '/download', icon: Download, keywords: ['descargar', 'instalar', 'agente'], category: 'pages' },
  ];

  const quickActions = [
    {
      name: 'Ver CVEs Críticas',
      path: '/vulnerabilities?severity=critical',
      icon: Bug,
      keywords: ['criticas', 'critical', 'urgente', 'alto riesgo'],
      category: 'actions',
      description: 'Filtrar vulnerabilidades críticas'
    },
    {
      name: 'Exportar Vulnerabilidades CSV',
      path: '/vulnerabilities?action=export',
      icon: FileDown,
      keywords: ['exportar', 'csv', 'download', 'descargar'],
      category: 'actions',
      description: 'Descargar lista de CVEs'
    },
    {
      name: 'Iniciar Escaneo de Red',
      path: '/network-scanner?action=scan',
      icon: Play,
      keywords: ['scan', 'escanear', 'iniciar', 'descubrir'],
      category: 'actions',
      description: 'Nuevo escaneo de puertos'
    },
    {
      name: 'Ver Agentes Offline',
      path: '/agent-health?status=offline',
      icon: Monitor,
      keywords: ['offline', 'desconectados', 'caidos', 'down'],
      category: 'actions',
      description: 'Agentes sin conexión'
    },
    {
      name: 'Refrescar Dashboard',
      path: '/?refresh=true',
      icon: RefreshCw,
      keywords: ['refresh', 'actualizar', 'recargar'],
      category: 'actions',
      description: 'Actualizar datos del dashboard'
    },
    {
      name: 'Exportar a SIEM',
      path: '/reports?tab=siem',
      icon: Radio,
      keywords: ['siem', 'cef', 'splunk', 'exportar'],
      category: 'actions',
      description: 'Exportar en formato SIEM'
    },
  ];

  // Known CVEs for quick search
  const knownCVEs = [
    { id: 'CVE-2020-1472', name: 'Zerologon', severity: 'critical' },
    { id: 'CVE-2021-34527', name: 'PrintNightmare', severity: 'critical' },
    { id: 'CVE-2021-26855', name: 'ProxyLogon', severity: 'critical' },
    { id: 'CVE-2017-0144', name: 'EternalBlue', severity: 'critical' },
    { id: 'CVE-2021-44228', name: 'Log4Shell', severity: 'critical' },
    { id: 'CVE-2023-23397', name: 'Outlook Elevation', severity: 'critical' },
    { id: 'CVE-2023-4863', name: 'WebP Heap Overflow', severity: 'high' },
  ];


  // Filter and categorize results
  const filteredResults = useMemo(() => {
    const searchLower = search.toLowerCase().trim();

    if (!searchLower) {
      // Show recent searches and quick actions when no search
      return {
        recent: recentSearches.slice(0, 3),
        actions: quickActions.slice(0, 4),
        pages: [],
        cves: [],
        agents: []
      };
    }

    // Filter pages
    const filteredPages = pages.filter(page =>
      page.name.toLowerCase().includes(searchLower) ||
      page.keywords.some(kw => kw.includes(searchLower))
    );

    // Filter actions
    const filteredActions = quickActions.filter(action =>
      action.name.toLowerCase().includes(searchLower) ||
      action.keywords.some(kw => kw.includes(searchLower))
    );

    // Filter CVEs
    const filteredCVEs = knownCVEs.filter(cve =>
      cve.id.toLowerCase().includes(searchLower) ||
      cve.name.toLowerCase().includes(searchLower)
    ).map(cve => ({
      name: `${cve.id} - ${cve.name}`,
      path: `/vulnerabilities?search=${cve.id}`,
      icon: Bug,
      category: 'cves',
      severity: cve.severity
    }));

    // Filter agents
    const filteredAgents = agents.filter(agent =>
      agent.hostname.toLowerCase().includes(searchLower) ||
      agent.ip.includes(searchLower)
    ).map(agent => ({
      name: agent.hostname,
      path: `/agent-health?agent=${encodeURIComponent(agent.hostname)}`,
      icon: Server,
      category: 'agents',
      description: `${agent.ip} - ${agent.os}`
    }));

    return {
      recent: [],
      actions: filteredActions,
      pages: filteredPages,
      cves: filteredCVEs,
      agents: filteredAgents
    };
  }, [search, recentSearches, agents]);

  // Get all items in a flat list for keyboard navigation
  const allItems = useMemo(() => {
    const items = [];
    if (filteredResults.recent.length > 0) {
      items.push(...filteredResults.recent.map(r => ({ ...r, section: 'recent' })));
    }
    if (filteredResults.actions.length > 0) {
      items.push(...filteredResults.actions.map(a => ({ ...a, section: 'actions' })));
    }
    if (filteredResults.pages.length > 0) {
      items.push(...filteredResults.pages.map(p => ({ ...p, section: 'pages' })));
    }
    if (filteredResults.cves.length > 0) {
      items.push(...filteredResults.cves.map(c => ({ ...c, section: 'cves' })));
    }
    if (filteredResults.agents.length > 0) {
      items.push(...filteredResults.agents.map(a => ({ ...a, section: 'agents' })));
    }
    return items;
  }, [filteredResults]);

  // Keyboard shortcut to open
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl+K or Cmd+K
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
      // Escape to close
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (!isOpen) return;
    apiClient.get('/agents')
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : res.data?.agents;
        setAgents((Array.isArray(list) ? list : []).map((a) => ({
          hostname: a.hostname || '',
          ip: a.ip_address || '',
          os: [a.os_type, a.os_version].filter(Boolean).join(' '),
        })));
      })
      .catch(() => setAgents([]));
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setSearch('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  const categories = ['all', 'pages', 'actions', 'cves', 'agents'];

  // Handle navigation within results
  const handleKeyNavigation = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev =>
        prev < allItems.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => prev > 0 ? prev - 1 : 0);
    } else if (e.key === 'Tab') {
      e.preventDefault();
      const currentIdx = categories.indexOf(activeCategory);
      const nextIdx = e.shiftKey
        ? (currentIdx - 1 + categories.length) % categories.length
        : (currentIdx + 1) % categories.length;
      setActiveCategory(categories[nextIdx]);
      setSelectedIndex(0);
    } else if (e.key === 'Enter' && allItems[selectedIndex]) {
      e.preventDefault();
      const item = allItems[selectedIndex];
      saveRecentSearch({ name: item.name, path: item.path, icon: item.icon?.name || 'Star' });
      navigate(item.path);
      setIsOpen(false);
    }
  }, [allItems, selectedIndex, navigate, saveRecentSearch, activeCategory]);

  // Reset selected index when search changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [search]);

  // Helper to get current item index
  const getItemIndex = (section, idx) => {
    let offset = 0;
    if (section === 'recent') return idx;
    offset += filteredResults.recent.length;
    if (section === 'actions') return offset + idx;
    offset += filteredResults.actions.length;
    if (section === 'pages') return offset + idx;
    offset += filteredResults.pages.length;
    if (section === 'cves') return offset + idx;
    offset += filteredResults.cves.length;
    if (section === 'agents') return offset + idx;
    return offset;
  };

  const getSeverityChip = (severity) => {
    switch (severity) {
      case 'critical': return 'text-red-400 bg-red-500/[0.08] border border-red-500/[0.2]';
      case 'high': return 'text-orange-400 bg-orange-500/[0.08] border border-orange-500/[0.2]';
      case 'medium': return 'text-yellow-400 bg-yellow-500/[0.08] border border-yellow-500/[0.2]';
      default: return 'text-slate-400 bg-slate-500/[0.08] border border-white/[0.08]';
    }
  };

  if (!isOpen) return null;

  const renderSection = (title, items, section, icon) => {
    if (items.length === 0) return null;
    const Icon = icon;

    return (
      <div className="px-2 mb-2">
        <div className="px-2 py-1.5 text-[10px] font-mono uppercase tracking-[0.22em] text-cyan-400/70 flex items-center gap-2">
          <Icon className="h-3 w-3" weight="regular" aria-hidden="true" />
          {title}
        </div>
        {items.map((item, index) => {
          const ItemIcon = typeof item.icon === 'function' ? item.icon : Star;
          const globalIndex = getItemIndex(section, index);
          const isSelected = globalIndex === selectedIndex;

          return (
            <button
              type="button"
              key={item.path + index}
              onClick={() => {
                saveRecentSearch({ name: item.name, path: item.path, icon: item.icon?.name || 'Star' });
                navigate(item.path);
                setIsOpen(false);
              }}
              onMouseEnter={() => setSelectedIndex(globalIndex)}
              aria-selected={isSelected}
              className={`
                w-full flex items-center gap-3 px-3 py-2.5 rounded-md
                transition-colors duration-150
                border-l-2
                ${isSelected
                  ? 'bg-cyan-500/[0.08] border-cyan-400 text-slate-100'
                  : 'bg-transparent border-transparent text-slate-300 hover:bg-cyan-500/[0.04]'
                }
              `}
            >
              <div className={`
                shrink-0 w-9 h-9 rounded-md flex items-center justify-center
                ${isSelected
                  ? 'bg-cyan-500/[0.12] border border-cyan-500/[0.2]'
                  : 'bg-slate-800/60 border border-white/[0.06]'
                }
              `}>
                <ItemIcon
                  className={`h-4 w-4 ${isSelected ? 'text-cyan-400' : 'text-slate-400'}`}
                  weight="regular"
                  aria-hidden="true"
                />
              </div>
              <div className="flex-1 text-left min-w-0">
                <div className="text-[13px] font-medium tracking-tight truncate">{item.name}</div>
                {item.description && (
                  <div className="text-[11px] font-mono text-slate-500 truncate">{item.description}</div>
                )}
                {!item.description && item.path && (
                  <div className="text-[11px] font-mono text-slate-500 truncate">{item.path}</div>
                )}
              </div>
              {item.severity && (
                <span className={`px-2 py-0.5 rounded-md text-[10px] font-mono uppercase tracking-wider font-medium ${getSeverityChip(item.severity)}`}>
                  {item.severity}
                </span>
              )}
              {isSelected && (
                <ArrowRight className="h-3.5 w-3.5 text-cyan-400 flex-shrink-0" weight="bold" aria-hidden="true" />
              )}
            </button>
          );
        })}
      </div>
    );
  };

  const hasResults = allItems.length > 0;
  const kbdCls = 'px-1.5 py-0.5 text-[10px] font-mono text-slate-300 bg-slate-800 rounded border border-white/[0.08]';

  // ── Query Mode: terminal-style command parser ───────────────────────
  // Activa cuando el input empieza por '>' o ':'. Estética Geist Mono + bg #0D1117.
  const queryMode = /^[>:]/.test(search.trimStart());
  const querySuggestions = [
    { cmd: 'show vulns critical', desc: 'navega a vulnerabilidades severidad crítica' },
    { cmd: 'show agents offline', desc: 'navega a agentes con estado offline' },
    { cmd: 'find <hostname>', desc: 'busca agente por hostname (ej. find DC-PRIMARY)' },
    { cmd: 'help', desc: 'muestra comandos disponibles' },
  ];

  const parseQuery = (raw) => {
    const text = raw.replace(/^[>:]\s*/, '').trim().toLowerCase();
    if (!text) return { kind: 'prompt' };
    if (text === 'help' || text === '?') return { kind: 'help' };
    if (text === 'show vulns critical' || text === 'show vulnerabilities critical') {
      return { kind: 'navigate', path: '/vulnerabilities?severity=critical', label: 'Abrir Vulnerabilidades · severity=critical' };
    }
    if (text === 'show agents offline') {
      return { kind: 'navigate', path: '/agents?status=offline', label: 'Abrir Agentes · status=offline' };
    }
    const findMatch = text.match(/^find\s+(.+)$/);
    if (findMatch) {
      const needle = findMatch[1].trim();
      const matches = agents.filter(a => a.hostname.toLowerCase().includes(needle));
      return { kind: 'find', needle, matches };
    }
    return { kind: 'unknown', text };
  };

  const queryResult = queryMode ? parseQuery(search) : null;

  const runQuery = () => {
    if (!queryResult) return;
    if (queryResult.kind === 'navigate') {
      navigate(queryResult.path);
      setIsOpen(false);
      setSearch('');
    }
  };

  return (
    <>
      {/* Backdrop — restraint, no drama */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
        onClick={() => setIsOpen(false)}
        aria-hidden="true"
      />

      {/* Command Palette */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Paleta de comandos"
        className="fixed top-[12%] left-1/2 -translate-x-1/2 w-full max-w-2xl z-[101] px-4"
      >
        <div
          className={`backdrop-blur-xl border rounded-xl shadow-2xl overflow-hidden transition-colors ${
            queryMode
              ? 'bg-v4-bg/98 border-cyan-500/[0.25]'
              : 'bg-slate-900/95 border-white/[0.08]'
          }`}
        >
          {/* Search Input */}
          <div className={`flex items-center gap-3 px-4 py-3.5 border-b ${queryMode ? 'border-cyan-500/[0.15]' : 'border-white/[0.06]'}`}>
            {queryMode ? (
              <span className="font-mono text-[13px] text-cyan-400 shrink-0 select-none" aria-hidden="true">&gt;</span>
            ) : (
              <Search className="h-4 w-4 text-cyan-400/80 shrink-0" weight="regular" aria-hidden="true" />
            )}
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (queryMode && e.key === 'Enter') {
                  e.preventDefault();
                  runQuery();
                  return;
                }
                handleKeyNavigation(e);
              }}
              placeholder={queryMode ? 'show vulns critical · find DC-PRIMARY · help' : 'Buscar páginas, acciones, CVEs, agentes... (>: para query mode)'}
              aria-label="Buscar"
              className={`flex-1 bg-transparent outline-none tracking-tight ${
                queryMode
                  ? 'font-mono text-[13px] text-cyan-100 caret-cyan-400 placeholder:text-cyan-500/40'
                  : 'text-slate-100 text-[15px] placeholder:text-slate-500'
              }`}
              spellCheck={false}
              autoComplete="off"
            />
            <div className="flex items-center gap-2">
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  aria-label="Limpiar búsqueda"
                  className="p-1 text-slate-500 hover:text-slate-200 hover:bg-v4-panel/[0.06] rounded-md transition-colors"
                >
                  <X className="h-3.5 w-3.5" weight="regular" aria-hidden="true" />
                </button>
              )}
              <kbd className={kbdCls} aria-hidden="true">ESC</kbd>
            </div>
          </div>

          {/* Query Mode: Terminal panel */}
          {queryMode && (
            <div className="px-4 py-4 max-h-[400px] overflow-y-auto font-mono text-[12.5px] leading-[1.65] text-cyan-100/90 bg-v4-bg">
              {queryResult?.kind === 'prompt' && (
                <div className="text-cyan-500/60">
                  <div className="text-cyan-400/80 mb-2">[VEKTOR // query-mode]</div>
                  <div className="text-slate-500 mb-3 text-[11px] uppercase tracking-[0.18em]">Sugerencias</div>
                  <ul className="space-y-1.5">
                    {querySuggestions.map((s) => (
                      <li key={s.cmd} className="flex items-baseline gap-3">
                        <span className="text-cyan-300">&gt; {s.cmd}</span>
                        <span className="text-slate-500 text-[11.5px]">— {s.desc}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {queryResult?.kind === 'help' && (
                <div>
                  <div className="text-cyan-400/80 mb-2">[help] comandos disponibles</div>
                  <ul className="space-y-1">
                    {querySuggestions.map((s) => (
                      <li key={s.cmd} className="flex items-baseline gap-3">
                        <span className="text-cyan-300 min-w-[180px]">{s.cmd}</span>
                        <span className="text-slate-500">{s.desc}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-3 text-slate-600 text-[11px]">
                    enter ejecuta · esc cierra · empieza el input con <span className="text-cyan-400">&gt;</span> o <span className="text-cyan-400">:</span> para query mode
                  </div>
                </div>
              )}

              {queryResult?.kind === 'navigate' && (
                <div>
                  <div className="text-emerald-400 mb-2">[match] {queryResult.label}</div>
                  <div className="text-slate-400">→ <span className="text-cyan-300">{queryResult.path}</span></div>
                  <div className="mt-3 text-slate-600 text-[11px]">
                    pulsa <span className="text-cyan-400">enter</span> para ejecutar
                  </div>
                </div>
              )}

              {queryResult?.kind === 'find' && (
                <div>
                  <div className="text-cyan-400/80 mb-2">[find] hostname ~ "{queryResult.needle}"</div>
                  {queryResult.matches.length === 0 ? (
                    <div className="text-amber-400/80">
                      sin coincidencias
                    </div>
                  ) : (
                    <ul className="space-y-1">
                      {queryResult.matches.map((a) => (
                        <li key={a.hostname} className="flex items-baseline gap-3">
                          <span className="text-cyan-300 min-w-[160px]">{a.hostname}</span>
                          <span className="text-slate-500">{a.ip}</span>
                          <span className="text-slate-600 text-[11px]">· {a.os}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {queryResult?.kind === 'unknown' && (
                <div>
                  <div className="text-red-400/80 mb-2">[error] comando no reconocido: "{queryResult.text}"</div>
                  <div className="text-slate-500">escribe <span className="text-cyan-400">help</span> para ver comandos disponibles</div>
                </div>
              )}
            </div>
          )}

          {/* Category Tabs */}
          {!queryMode && (
          <>
          <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/[0.06] overflow-x-auto">
            {[
              { id: 'all', label: 'Todo' },
              { id: 'pages', label: 'Páginas' },
              { id: 'actions', label: 'Acciones' },
              { id: 'cves', label: 'CVEs' },
              { id: 'agents', label: 'Agentes' },
            ].map(tab => (
              <button
                type="button"
                key={tab.id}
                onClick={() => setActiveCategory(tab.id)}
                aria-pressed={activeCategory === tab.id}
                className={`px-2.5 py-1 text-[11px] font-mono uppercase tracking-wider rounded-md transition-colors whitespace-nowrap
                  ${activeCategory === tab.id
                    ? 'bg-cyan-500/[0.1] text-cyan-300 border border-cyan-500/[0.25]'
                    : 'text-slate-400 hover:text-slate-100 hover:bg-white/[0.04] border border-transparent'
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Results */}
          <div className="max-h-[400px] overflow-y-auto py-2" role="listbox">
            {!hasResults ? (
              <div className="px-4 py-10 text-center">
                <Search className="h-7 w-7 mx-auto mb-3 text-slate-600" weight="regular" aria-hidden="true" />
                <p className="text-[13px] text-slate-400">
                  Sin resultados para <span className="font-mono text-slate-300">"{search}"</span>
                </p>
                <p className="text-[11px] font-mono uppercase tracking-wider text-slate-600 mt-2">
                  Intenta CVE, hostname o página
                </p>
              </div>
            ) : (
              <>
                {(activeCategory === 'all' || activeCategory === 'pages') &&
                  filteredResults.recent.length > 0 &&
                  renderSection('Recientes', filteredResults.recent, 'recent', Clock)}

                {(activeCategory === 'all' || activeCategory === 'actions') &&
                  renderSection('Acciones Rápidas', filteredResults.actions, 'actions', Zap)}

                {(activeCategory === 'all' || activeCategory === 'pages') &&
                  renderSection('Páginas', filteredResults.pages, 'pages', FileText)}

                {(activeCategory === 'all' || activeCategory === 'cves') &&
                  renderSection('Vulnerabilidades (CVEs)', filteredResults.cves, 'cves', Bug)}

                {(activeCategory === 'all' || activeCategory === 'agents') &&
                  renderSection('Agentes', filteredResults.agents, 'agents', Server)}
              </>
            )}
          </div>
          </>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-white/[0.06] bg-slate-950/40">
            <div className="flex items-center gap-4 text-[11px] text-slate-500">
              <div className="flex items-center gap-1">
                <kbd className={kbdCls} aria-hidden="true">↑</kbd>
                <kbd className={kbdCls} aria-hidden="true">↓</kbd>
                <span className="ml-1 font-mono uppercase tracking-wider">Navegar</span>
              </div>
              <div className="flex items-center gap-1">
                <kbd className={kbdCls} aria-hidden="true">Enter</kbd>
                <span className="ml-1 font-mono uppercase tracking-wider">Ir</span>
              </div>
              <div className="flex items-center gap-1">
                <kbd className={kbdCls} aria-hidden="true">Tab</kbd>
                <span className="ml-1 font-mono uppercase tracking-wider">Categoría</span>
              </div>
            </div>
            <div className="flex items-center gap-1 text-[11px] text-slate-500 font-mono uppercase tracking-wider">
              <Command className="h-3 w-3" weight="regular" aria-hidden="true" />
              <span>+ K</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CommandPalette;
